import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiJson } from '@/lib/api';
import { formatPacificDateTime } from '@/lib/adminPacificTime';
import { formatShortDate } from '@/lib/utils';
import type { AdminUserDetailResponse, JournalEntryListItem } from '@/lib/types';

function AdminUserJournalPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hid = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? '', [searchStr]);
  const hidNum = Number(hid);

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const canView =
    !!hid &&
    Number.isFinite(hidNum) &&
    (userQuery.data?.households.some((h) => h.household_id === hidNum) ?? false);

  const journalQuery = useQuery({
    queryKey: ['admin', 'household-journal', hidNum, id],
    queryFn: () =>
      apiJson<JournalEntryListItem[]>(
        getToken,
        `/v1/admin/households/${hidNum}/journal?creator_user_id=${id}`,
      ),
    enabled: canView,
  });

  if (!hid || !canView) {
    return <p className='text-sm text-nova-muted-foreground'>Select a valid household (use the household chips above).</p>;
  }

  const entries = journalQuery.data ?? [];
  const isLoading = journalQuery.isLoading;
  const isError = journalQuery.isError;

  return (
    <div className='space-y-4'>
      <h2 className='text-lg font-semibold text-nova-foreground'>
        Journal · household #{hidNum}
      </h2>
      <p className='text-sm text-nova-muted-foreground'>
        Entries created by this user in the selected household (newest first).
      </p>

      {isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : isError ? (
        <p className='text-sm text-red-600'>Could not load journal entries.</p>
      ) : entries.length === 0 ? (
        <p className='text-sm text-nova-muted-foreground bg-nova-card rounded-lg border border-nova-border p-4'>No entries.</p>
      ) : (
        <ul className='space-y-3'>
          {entries.map((entry) => (
            <li
              key={entry.id}
              className='bg-nova-card rounded-lg border border-nova-border shadow-sm p-4 text-sm'
            >
              <div className='flex flex-wrap items-start justify-between gap-2 border-b border-nova-border pb-2 mb-2'>
                <div className='min-w-0'>
                  <p className='font-semibold text-nova-foreground truncate'>
                    {entry.title?.trim() || (entry.entry_type === 'PHONE_CALL' ? 'Phone call' : 'Untitled')}
                  </p>
                  <p className='text-xs text-nova-muted-foreground mt-0.5'>
                    <span className='tabular-nums'>{formatShortDate(entry.entry_date)}</span>
                    <span className='text-nova-muted-foreground'> · </span>
                    <span className='font-mono text-nova-muted-foreground'>{entry.entry_type}</span>
                    <span className='text-nova-muted-foreground'> · </span>
                    <span>#{entry.id}</span>
                  </p>
                </div>
                {entry.attachment_count != null && entry.attachment_count > 0 ? (
                  <span className='text-xs font-medium text-nova-muted-foreground bg-nova-muted rounded-md px-2 py-0.5 shrink-0'>
                    {entry.attachment_count} attachment{entry.attachment_count === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>

              {entry.entry_type === 'PHONE_CALL' ? (
                <p className='text-xs text-nova-muted-foreground mb-2'>
                  {[entry.call_direction, entry.call_type].filter(Boolean).join(' · ')}
                  {entry.call_duration_minutes != null ? ` · ${entry.call_duration_minutes} min` : null}
                </p>
              ) : null}

              {entry.body?.trim() ? (
                <p className='text-nova-foreground whitespace-pre-wrap line-clamp-4'>{entry.body.trim()}</p>
              ) : (
                <p className='text-xs text-nova-muted-foreground italic'>No body text</p>
              )}

              {(entry.tags?.length ?? 0) > 0 ? (
                <div className='flex flex-wrap gap-1.5 mt-3'>
                  {entry.tags!.map((t) => (
                    <span
                      key={t.id}
                      className='text-[10px] font-semibold px-2 py-0.5 rounded-md border border-nova-border'
                      style={{ color: t.color, borderColor: `${t.color}55` }}
                    >
                      {t.label}
                    </span>
                  ))}
                </div>
              ) : null}

              <p className='text-[10px] text-nova-muted-foreground mt-2 tabular-nums'>
                Logged {formatPacificDateTime(entry.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/journal')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserJournalPage,
});
